import type { Map as MapboxMap, Point } from 'mapbox-gl';
import { LayerId } from '../constants';
import { dealFeatureAt } from './dealHitTest';
import { COMPETITOR_TIER_LAYER_IDS } from './MapLayersService';
import { paintedPlaceMaki } from './makiPaths';

/** Standard's points of interest live on the basemap import, not a root layer. */
export const BASEMAP_POI_TARGET = { featuresetId: 'poi', importId: 'basemap' } as const;

export interface PointOfInterestPlace {
  name: string;
  longitude: number;
  latitude: number;
  /** Icon the basemap paints. A transit stop uses its network, then its mode. */
  maki: string;
  /** Basemap category. A transit stop uses `transit_stop`, the blue circle on that layer. */
  placeClass: string;
}

/** What a basemap interaction and the tests both hand us. Geometry stays Mapbox's own. */
type PlaceFeature = {
  properties?: Record<string, unknown> | null;
  geometry?: GeoJSON.Geometry | null;
};

function textProperty(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

/** A named place with an icon. A dot with no name, or a bare marker, is not something to open. */
export function placeFromPointOfInterest(
  feature: PlaceFeature | undefined,
): PointOfInterestPlace | null {
  const properties = feature?.properties;
  const name = textProperty(properties?.name_en) || textProperty(properties?.name);
  const geometry = feature?.geometry;
  if (!name || geometry?.type !== 'Point') return null;
  const [longitude, latitude] = geometry.coordinates;
  if (longitude == null || latitude == null) return null;
  if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) return null;
  const maki = paintedPlaceMaki(
    textProperty(properties?.maki),
    textProperty(properties?.maki_beta),
    textProperty(properties?.transit_network),
    textProperty(properties?.transit_mode),
  );
  if (!maki || maki === 'marker') return null;
  const placeClass =
    textProperty(properties?.class) ||
    (textProperty(properties?.group) === 'transit' ? 'transit_stop' : '');
  return {
    name,
    longitude,
    latitude,
    maki,
    placeClass,
  };
}

/** Marks drawn above a place. A click on one of these is not a place click.
 *  A hunting zone is not one of them: its fill sits under the place icon. */
const MARK_LAYERS = [
  ...COMPETITOR_TIER_LAYER_IDS,
  LayerId.CLUSTER_CHIP,
  LayerId.LOCATION_UNCLUSTERED,
  LayerId.SELECTED_LOCATION_MARK,
  LayerId.STATION_MODEL_LOCATIONS,
  LayerId.STATION_MODEL_SELECTED_LOCATION,
];

/** Stations, deals, and location pins sit above a place and keep the click. */
export function mapMarkAt(map: MapboxMap, point: Point): boolean {
  if (dealFeatureAt(map, point)) return true;
  const layers = MARK_LAYERS.filter((id) => map.getLayer(id));
  if (layers.length === 0) return false;
  return map.queryRenderedFeatures(point, { layers }).length > 0;
}

/**
 * The painted icon is larger than the collision box a point query uses, and the
 * title hangs below the anchor. A click on the glyph misses the point and lands
 * on the zone fill underneath, so the hit is a small box around the pointer.
 */
export const PLACE_HIT_PADDING_PX = 16;

/** Squared pixels from the pointer to the place anchor. Ties keep the earlier feature. */
function pointerDistance(map: MapboxMap, point: Point, place: PointOfInterestPlace): number {
  if (typeof map.project !== 'function') return 0;
  const projected = map.project([place.longitude, place.latitude]);
  const dx = projected.x - point.x;
  const dy = projected.y - point.y;
  return dx * dx + dy * dy;
}

/**
 * The named place under the pointer. The box can hold the next icon too, so the
 * one whose anchor is closest to the click is the one that was aimed at.
 */
export function placeAt(map: MapboxMap, point: Point): PointOfInterestPlace | null {
  const { x, y } = point;
  const hitBox: [[number, number], [number, number]] = [
    [x - PLACE_HIT_PADDING_PX, y - PLACE_HIT_PADDING_PX],
    [x + PLACE_HIT_PADDING_PX, y + PLACE_HIT_PADDING_PX],
  ];
  try {
    let closest: PointOfInterestPlace | null = null;
    let closestDistance = Number.POSITIVE_INFINITY;
    for (const feature of map.queryRenderedFeatures(hitBox, { target: BASEMAP_POI_TARGET })) {
      const place = placeFromPointOfInterest(feature);
      if (!place) continue;
      const distance = pointerDistance(map, point, place);
      if (distance < closestDistance) {
        closest = place;
        closestDistance = distance;
      }
    }
    return closest;
  } catch {
    return null;
  }
}

/** A named place icon under the pointer. The zone polygon around it does not count. */
export function namedPlaceAt(map: MapboxMap, point: Point): boolean {
  return placeAt(map, point) != null;
}
